import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';

import { Position } from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { CollectionSlots } from 'components/collections/CollectionSlots';

import {
  CollectionCardsQuery,
  CollectionCardsQueryVariables,
} from './__generated__/index.graphql';

const COLLECTION_CARDS_QUERY = gql`
  query CollectionCardsQuery($collection: String!, $slug: String!) {
    cardCollection(slug: $collection) {
      slug
      ...CollectionSlots_cardCollection
      slots {
        id
        ...CollectionSlots_cardCollectionSlot
      }
      userCardCollection(forUserSlug: $slug) {
        id
        fulfilledSlotsCount
        slots {
          slug
          ...CollectionSlots_userCardCollectionSlot
        }
      }
    }
  }
  ${CollectionSlots.fragments.userCardCollectionSlot}
  ${CollectionSlots.fragments.cardCollectionSlot}
  ${CollectionSlots.fragments.cardCollection}
` as TypedDocumentNode<CollectionCardsQuery, CollectionCardsQueryVariables>;

type Props = {
  collection: string;
  slug: string;
  groupBy?: {
    id: string;
    label: ReactNode;
    positions: Position[];
  }[];
};

const CardsPage = ({ collection, slug, groupBy }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { data } = useQuery(COLLECTION_CARDS_QUERY, {
    variables: {
      collection,
      slug,
    },
  });
  const { cardCollection } = data || {};
  const { userCardCollection } = cardCollection || {};

  if (!cardCollection) {
    return null;
  }

  const slots =
    userCardCollection && userCardCollection?.fulfilledSlotsCount > 0
      ? userCardCollection?.slots
      : cardCollection?.slots;

  return (
    <CollectionSlots
      cardCollection={cardCollection}
      slots={slots || []}
      groupBy={groupBy}
      readOnly={slug !== currentUser?.slug}
    />
  );
};

export default CardsPage;
